import {Form, Select, Radio} from 'antd';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';
import styles from './geoLocationConfig.module.scss';
import {
  ASSET_CLASS_KEY_OPTIONS,
  INSTRUMENT_KEY_GROUPS,
  OPERATION_OPTIONS,
  aclActionLabel,
  getDefaultRule,
  shadowingAclActions,
} from './regionAclOptions';

/**
 * The instrument restrictions on one region entry — `regionAcl[].rules`.
 *
 * A rule is four independent filters, and every one of them widens to "all"
 * when left empty. That is why each select says "All …" rather than sitting
 * blank: an empty box otherwise reads as "nothing selected, so nothing
 * restricted", which is the opposite of what it means.
 *
 * Rules are what let a restriction name a venue. `acl` cannot: it predates the
 * second exchange and can only block an action everywhere.
 */
export const RegionRules = ({form, fieldName, exchanges, loadingExchanges}) => {
  // Watched at the list root. A watch of ['regionAcl', fieldName] resolves to
  // undefined — `useWatch` only tracks paths that are registered fields, and an
  // entry object is not one.
  const entry = Form.useWatch('regionAcl', form)?.[fieldName];

  const dropAclAction = (action, acl) =>
    form.setFields([
      {
        name: ['regionAcl', fieldName, 'acl'],
        value: (acl || []).filter(item => item !== action),
      },
    ]);

  return (
    <div className={styles.rulesSection}>
      <div className={styles.rulesHeader}>
        <h3>Instrument Restrictions</h3>
        <p>
          Each rule blocks the instruments it names, on the venues it names.
          Every list left empty means <strong>all</strong> — a rule with nothing
          filled in blocks every instrument on every exchange.
        </p>
      </div>

      <Form.List name={[fieldName, 'rules']}>
        {(ruleFields, {add, remove}) => (
          <>
            {ruleFields.map(({key, name: ruleName}) => (
              <div className={styles.ruleCard} key={key}>
                <div className={styles.ruleCardHeader}>
                  <span>Rule {ruleName + 1}</span>
                  <img
                    src={deleteIcon}
                    alt="Delete rule"
                    className={styles.deleteButton}
                    onClick={() => remove(ruleName)}
                  />
                </div>

                <Form.Item
                  label="Exchanges"
                  className={styles.fullWidth}
                  name={[ruleName, 'exchanges']}
                  tooltip="Leave empty to restrict the instruments below on every venue.">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={loadingExchanges}
                    placeholder="All exchanges"
                    // Selection is by code, not id: a code survives an exchange
                    // being deleted and recreated, an id does not.
                    options={exchanges.map(exchange => ({
                      value: exchange.code,
                      label: `${exchange.stockExchangeName} (${exchange.code})`,
                    }))}
                  />
                </Form.Item>

                {shadowingAclActions(entry?.rules?.[ruleName], entry?.acl).map(
                  action => (
                    <div className={styles.shadowedRule} key={action}>
                      <div>
                        <strong>{aclActionLabel(action)}</strong> is still in
                        this region&apos;s action list, which blocks it on{' '}
                        <strong>every</strong> exchange before this rule is
                        reached. Leaving it there keeps the other venues
                        restricted and this rule changes nothing.
                      </div>
                      <button
                        type="button"
                        onClick={() => dropAclAction(action, entry?.acl)}>
                        Remove {aclActionLabel(action)}
                      </button>
                    </div>
                  ),
                )}

                <div className={styles.rowFields}>
                  <Form.Item
                    label="Asset Classes"
                    className={styles.halfWidth}
                    name={[ruleName, 'assetClassKeys']}>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="All asset classes"
                      options={ASSET_CLASS_KEY_OPTIONS}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Instrument Types"
                    className={styles.halfWidth}
                    name={[ruleName, 'instrumentKeys']}>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="All instrument types"
                      options={INSTRUMENT_KEY_GROUPS}
                    />
                  </Form.Item>
                </div>

                {/* The one thing about this form that reads backwards. The two
                  lists above are added together, not intersected: an admin who
                  fills both expecting "public equity within equities" gets all
                  of equities, and nothing surfaces the mistake except support
                  tickets. */}
                <p className={styles.ruleHint}>
                  Both lists are added together. Selecting an asset class
                  restricts everything inside it — to restrict a single
                  instrument type, leave <strong>Asset Classes</strong> empty.
                </p>

                <Form.Item
                  label="Direction"
                  className={styles.halfWidth}
                  name={[ruleName, 'operation']}>
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    options={OPERATION_OPTIONS}
                  />
                </Form.Item>
              </div>
            ))}

            <div
              className={styles.addRuleButton}
              onClick={() => add(getDefaultRule())}>
              <img src={plusIcon} alt="Add" />
              <h1>Add Instrument Restriction</h1>
            </div>
          </>
        )}
      </Form.List>
    </div>
  );
};
